import { useState } from "react";
import { Modal } from "antd";
import { FileExclamationOutlined, FileSyncOutlined, RollbackOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import { useBillHistoryById } from "../../../../../hooks/useReports";
import { useBillOperations } from "../../../../../hooks/useBillOperations";
import { useAppSelector } from "../../../../../store/hooks";
import type { Invoice } from "../../../../../api/invoices";
import { useBillOperationsActions } from "../../../../reports/components/billOperations/useBillOperationsActions";
import { ChoiceRow } from "../../ChoiceRow";
import { creditChoiceRows } from "./fiscalInvoiceCreditChoice";
import {
  DangerButton,
  GhostButton,
  ModalFooter,
  ModalHeader,
  posModalStyles as modalStyles,
} from "../../modalPrimitives";
import { blockIfLicenseReadOnly } from "../../../../../lib/licenseReadOnly";

/** Правото на „Сторнирай всичко“ (BE `cancel-all-items`) — „История на маси“. */
const STORNO_PERMISSION = "view_tables_history";

interface Props {
  invoice: Invoice;
  /** След изпълнено действие — прегледът се затваря (справката се опреснява сама). */
  onDone: () => void;
  /** `ModalFooter` го подава на тесен екран. */
  height?: number;
}

/**
 * „Кредитно известие“ в прегледа на фискална фактура (Справки → „Фактури“).
 *
 * Фискалната фактура не се анулира — кредитира се: известието излиза от
 * фискалното устройство за цялата сметка и парите се връщат в брой. Двата пътя
 * са тези от „История на операциите“ (`useBillOperationsActions`, същите
 * въпроси и съобщения): „Нова фактура“ = „Възстанови“ (сметката се отваря
 * наново и се закрива с фактура към правилния купувач), „Връщане на парите“ =
 * „Сторнирай всичко“. Сметката се тегли чак при натискане.
 */
export function FiscalInvoiceCreditAction({ invoice, onDone, height }: Props) {
  const { t } = useTranslation();
  const { isMobile, isNarrow } = useViewport();
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const billId = requested ? (invoice.bill_ids?.[0] ?? null) : null;
  const { data: bill = null, isLoading: billLoading } = useBillHistoryById(billId);
  const { data: operations } = useBillOperations(bill?.id ?? null);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const actions = useBillOperationsActions({
    bill,
    summary: operations?.summary,
    onClose: onDone,
  });
  const rows = creditChoiceRows(t, {
    invoiceNumber: invoice.invoice_number,
    bill,
    billLoading: requested && billLoading,
    summaryLoaded: !!operations?.summary,
    canCancel: actions.canCancel,
    canRestore: actions.canRestore,
    restoreHint: actions.restoreHint,
    hasStornoPermission: permissions.some((permission) => permission.name === STORNO_PERMISSION),
  });

  const choose = (run: () => void) => {
    setOpen(false);
    run();
  };

  return (
    <>
      <DangerButton
        label={t("fiscalInvoice.storno.action")}
        onClick={() => {
          if (blockIfLicenseReadOnly()) return;
          setRequested(true);
          setOpen(true);
        }}
        loading={actions.cancelLoading || actions.restoreLoading}
        icon={<FileExclamationOutlined />}
        height={height}
      />
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={isMobile ? undefined : 560}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div className="bg-pos-card text-pos-ink">
          <ModalHeader
            icon={<FileExclamationOutlined />}
            title={t("fiscalInvoice.storno.confirmTitle", { number: invoice.invoice_number })}
            subtitle={t("fiscalInvoice.credit.subtitle")}
            onClose={() => setOpen(false)}
            isNarrow={isNarrow}
          />
          <div className={`flex flex-col gap-2.5 ${isNarrow ? "px-4 py-4" : "px-6 py-5"}`}>
            <ChoiceRow
              icon={<FileSyncOutlined />}
              kicker={t("fiscalInvoice.credit.reissueKicker")}
              title={t("fiscalInvoice.credit.reissue")}
              description={t("fiscalInvoice.credit.reissueHint")}
              highlighted={false}
              showEnterHint={false}
              blockedReason={rows.reissue.blockedReason}
              pendingText={rows.reissue.pendingText}
              isNarrow={isNarrow}
              onClick={() => choose(actions.handleRestoreAll)}
            />
            <ChoiceRow
              icon={<RollbackOutlined />}
              kicker={t("fiscalInvoice.credit.refundKicker")}
              title={t("fiscalInvoice.credit.refund")}
              description={t("fiscalInvoice.credit.refundHint")}
              highlighted={false}
              showEnterHint={false}
              blockedReason={rows.refund.blockedReason}
              pendingText={rows.refund.pendingText}
              isNarrow={isNarrow}
              onClick={() => choose(actions.handleCancelAll)}
            />
          </div>
          <ModalFooter isNarrow={isNarrow}>
            <GhostButton label={t("common.cancel")} onClick={() => setOpen(false)} />
          </ModalFooter>
        </div>
      </Modal>
    </>
  );
}
